import React, { useContext, useEffect, useState } from "react";
import { ExpandMore } from "@mui/icons-material";
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Dialog, DialogActions, DialogContent, Grid, Paper, Typography, useTheme } from "@mui/material";
import { useForm } from "react-hook-form";
import {useNavigate, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {useMutation, useQuery} from "@tanstack/react-query";
import { yupResolver } from "@hookform/resolvers/yup";
import SectionHeader from "../../../util/component/SectionHeader";
import { setSnackbar } from "../../../util/state/globalAction";
import { GlobalContext } from "../../../util/state/GlobalContextProvider";
import { snackbarTypes } from "../../../util/state/globalVariables";
import ContentBody from "../common/content-body/ContentBody";
import { FormAutocomplete, FormDatePicker, FormTextField } from "../common/form-fields-wrappers";
import { RA_STATUS_OPTIONS } from "../ra-search/config";
import { VOID_MANAGEMENT_VALIDATION_SCHEMA } from "./config";
import { fetchRaDetail, sendVoid } from "./services/http.services";
import {AxiosError} from "axios";

const VoidManagement = () => {
    const navigate = useNavigate();
    const {t} = useTranslation(['network', 'translation']);
    const theme = useTheme();

    const params = useParams();
    const [open, setOpen] = useState(false);
    const [accordions, setAccordions] = useState([true, true]);

    const {dispatchGlobalData} = useContext(GlobalContext);
    const raStatusOptions = RA_STATUS_OPTIONS;

    const documentsForm = useForm({
        defaultValues: {
            idRentalAgreement: "",
            raNumber : '',
            dmsIdentificationCode: null,
            pickUpDate: '',
            renewalsAndSubstitutions: '',
            reasons: ''
        },
        resolver: yupResolver(VOID_MANAGEMENT_VALIDATION_SCHEMA)
    });

    const saveVoid = useMutation(
        sendVoid,
        {
            onSuccess: () => {
                navigate('/network/ra-search');
            },
            onError: (err: AxiosError) => {
                let mess = 'translation:error.tryLater';
                // @ts-ignore
                if (err.response?.status === 400 && err.response?.data?.errorDetails === "\"ERROR_INCONSISTENT_RA_STATE\"") {
                    mess = "common.error.errorInconsistentRaState"
                }
                dispatchGlobalData({
                    type: 'SET_SNACKBAR',
                    payload: {
                        snackbar: {
                            message: t(mess),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
            }
        }
    );

    const handleSaveVoid = (data: any) => {
        const payload = {
            idRentalAgreement : documentsForm.getValues("idRentalAgreement"),
            raState: {code: "VOID", description: "Void", label:"VOID"},
            reasons: documentsForm.getValues("reasons")
        }

        handleClose();
        saveVoid.mutate(payload);
    }

    const handleDiscardChanges = () => {
        documentsForm.setValue("reasons", "");
        setOpen(false);
    }

    const handleBack = () => {
        if (params.id) {
            navigate(`/network/ra-detail/${params.id}`);
        } else {
            navigate('/network/ra-search');
        }
    }

    const handleOpen = () => {
        setOpen(true);
    }

    const handleClose = () => {
        setOpen(false);
    }

    const onSuccessFetchRaDetail = (data: any) => {
        documentsForm.setValue("raNumber", data.generalInfo.raNumber);
        documentsForm.setValue("dmsIdentificationCode", data.generalInfo.dmsIdentificationCode);
        documentsForm.setValue("pickUpDate", data.generalInfo.pickupDate);
        documentsForm.setValue("renewalsAndSubstitutions", data.generalInfo.renewalsAndSubstitutions);
        documentsForm.setValue("idRentalAgreement", data.idRentalAgreement);
    }

    const onErrorFetchRaDetail = (error: any) => {
        dispatchGlobalData({
            type: setSnackbar,
            payload: {
                snackbar: {
                    message: "error",
                    type: snackbarTypes.error,
                    show: true,
                    autoClose: true
                }
            }
        });
    }

    const handleAccordionOnChange = (e: React.SyntheticEvent, expanded: boolean, index: number) => {
        e.stopPropagation();
        const acc = accordions.concat([]);
        acc[index] = expanded;
        setAccordions(acc);
    }

    const {isFetching, refetch} = useQuery(['raDetail', {idRentalAgreement: params.id}], () => fetchRaDetail({idRentalAgreement: params.id}), {
        enabled: false,
        onSuccess: (data) => onSuccessFetchRaDetail(data),
        onError: (error) => onErrorFetchRaDetail(error)
    })

    useEffect(() => {
        refetch();
    }, []);

    return (
        <>
            <SectionHeader title={t("voidManagement.header.title")}/>

            <ContentBody>
                <Paper elevation={1} sx={{p: theme.spacing(2), mb: theme.spacing(2)}}>
                    <form>
                        <Accordion expanded={accordions[0]}
                                   onChange={(e, expanded) => handleAccordionOnChange(e, expanded, 0)}>
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Typography variant={"h6"}>
                                    {t('voidManagement.form.label.raInfo')}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Grid container columnSpacing={2} sx={{mb: theme.spacing(4)}}>
                                    <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                                        <FormTextField
                                            name='raNumber'
                                            label={t('voidManagement.form.label.raNumber')}
                                            control={documentsForm.control}
                                            fullWidth
                                            disabled
                                        />
                                    </Grid>
                                    <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                                        <FormAutocomplete
                                            name='dmsIdentificationCode'
                                            label={t('voidManagement.form.label.dmsIdentificationCode')}
                                            control={documentsForm.control}
                                            options={raStatusOptions}
                                            isOptionEqualToValue={(option: any, value: any) => option?.code === value?.code}
                                            getOptionLabel={(opt: any) => opt?.description}
                                            fullWidth
                                            disabled
                                        />
                                    </Grid>
                                    <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                                        <FormDatePicker
                                            name="pickUpDate"
                                            control={documentsForm.control}
                                            label={t('voidManagement.form.label.pickUpDate')}
                                            disabled
                                        />
                                    </Grid>
                                    <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                                        <FormTextField
                                            name="renewalsAndSubstitutions"
                                            control={documentsForm.control}
                                            label={t('voidManagement.form.label.renewalsAndSubstitutions')}
                                            disabled
                                        />
                                    </Grid>
                                </Grid>
                            </AccordionDetails>
                        </Accordion>

                        <Accordion expanded={accordions[1]} onChange={(e, expanded) => handleAccordionOnChange(e, expanded, 1)}>
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Typography variant={"h6"}>
                                    {t('voidManagement.form.label.voidInfo')}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Grid container columnSpacing={2} sx={{mb: theme.spacing(4)}}>
                                    <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                                        <FormTextField
                                            name='reasons'
                                            label={t('voidManagement.form.label.reasons')}
                                            control={documentsForm.control}
                                        />
                                    </Grid>
                                </Grid>
                            </AccordionDetails>
                        </Accordion>

                        <Box sx={{display: "flex", justifyContent: "space-between", mt: theme.spacing(2)}}>
                            <Button variant={"outlined"} color={'secondary'} onClick={handleBack}>
                                {t('voidManagement.button.back')}
                            </Button>
                            <Box>
                                <Button variant={"outlined"} color={"accent"}
                                        sx={{mx: theme.spacing(1)}}
                                        onClick={handleDiscardChanges}>
                                    {t('voidManagement.button.cancel')}
                                </Button>

                                <Button color={"accent"}
                                        sx={{ml: theme.spacing(1)}}
                                        onClick={documentsForm.handleSubmit(handleOpen)}>
                                    {t('voidManagement.button.continue')}
                                </Button>
                            </Box>
                        </Box>

                        <Dialog
                            open={open}
                            onClose={handleClose}
                            aria-labelledby="alert-dialog-title"
                            aria-describedby="alert-dialog-description"
                        >
                            <DialogContent>
                                {t('voidManagement.modal.save.text')}
                            </DialogContent>
                            <DialogActions>
                                <Box sx={{display: "flex", justifyContent: "space-between", px: theme.spacing(2)}}>
                                    <Button color={"accent"}
                                            sx={{mx: theme.spacing(1)}}
                                            onClick={handleSaveVoid}>
                                        {t('voidManagement.button.continue')}
                                    </Button>
                                    <Button variant={"outlined"}
                                            color={"secondary"}
                                            sx={{mx: theme.spacing(1)}}
                                            onClick={handleDiscardChanges}>
                                        {t('voidManagement.button.cancel')}
                                    </Button>
                                </Box>
                            </DialogActions>
                        </Dialog>
                    </form>
                </Paper>
            </ContentBody>
        </>
    );
}

export default VoidManagement;
